import { ReactNode } from "react";

import { PracticeCrop, StructuredError } from "models";

import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";

export enum CMC_SOIL_TYPE {
  SAND = "sand",
  CLAY = "clay",
  LOAM = "loam",
  SILT = "silt",
}

export enum CMC_PH_TYPE {
  ACIDIC = "acidic",
  NEUTRAL = "neutral",
  ALKALINE = "alkaline",
}

export enum GOAL_LEVEL_1 {
  SOIL_HEALTH = "soilHealth",
  NUTRIENT_DYNAMIC = "nutrientDynamic",
  WEED_SUPPRESSION = "weedSuppression",
  DROUGHT_RESILIENCE = "droughtResilience",
  BIODIVERSITY = "biodiversity",
  USE_STRATEGY = "useStrategy",
}

export enum GOAL_LEVEL_2 {
  BUILD_CARBON = "buildCarbon",
  SOIL_STRUCTURE_TOPSOIL = "soilStructureTopsoil",
  SOIL_STRUCTURE_SUBSOIL = "soilStructureSubsoil",
  EROSION_CONTROL = "erosionControl",
  NUTRIENT_BINDING_FALL = "nutrientBindingFall",
  NUTRIENT_BINDING_SPRING = "nutrientBindingSpring",
  NUTRIENT_BINDING_TOTAL = "nutrientBindingTotal",
  NUTRIENT_ACCUMULATION_NITROGEN = "nutrientAccumulationNitrogen",
  NUTRIENT_ACCUMULATION_PHOSPHORUS = "nutrientAccumulationPhosphorus",
  WEED_SUPPRESSION_GROWTH = "weedSuppressionGrowth",
  WEED_SUPPRESSION_RESIDUE = "weedSuppressionResidue",
  DROUGHT_RESILIENCE_GERMINATION = "droughtResilienceGermination",
  DROUGHT_RESILIENCE_GROWTH = "droughtResilienceGrowth",
  INSECTS_AND_WILDLIFE = "insectsAndWildlife",
  FODDER = "fodder",
  GRAZING = "grazing",
  BIOGAS = "biogas",
  CUT_AND_CARRY = "cutAndCarry",
}

export enum SEED_COST_RATE {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum CROP_ROTATION {
  POACEAE = "poaceae",
  BRASSICACEAE = "brassicaceae",
  ASTERACEAE = "asteraceae",
  FABACEAE = "fabaceae",
  POLYGONACEAE = "polygonaceae",
  SOLANACEAE = "solanaceae",
  HYDROPHYLLACEAE = "hydrophyllaceae",
  LINACEAE = "linaceae",
  AMARANTHACEAE = "amaranthaceae",
  OTHER = "other",
}

export enum CMC_STEP_4_TOGGLE_OPTIONS {
  WEIGHT_SHARE = "weightShare",
  SEED_SHARE = "seedShare",
}

export enum CropRotationCompatibility {
  WINTER_OILSEED_RAPE = "WINTER_OILSEED_RAPE",
  LEGUMES = "LEGUMES",
  SUGAR_BEET = "SUGAR_BEET",
  CORN = "CORN",
  POTATOES = "POTATOES",
  WINTER_OILSEED_RAPE_OPTIONAL = "WINTER_OILSEED_RAPE_OPTIONAL",
  LEGUMES_OPTIONAL = "LEGUMES_OPTIONAL",
  SUGAR_BEET_OPTIONAL = "SUGAR_BEET_OPTIONAL",
  CORN_OPTIONAL = "CORN_OPTIONAL",
  POTATOES_OPTIONAL = "POTATOES_OPTIONAL",
}

export enum CMC_MIX_TYPE {
  AUTO_GENERATED = "auto-generated",
  AUTO_GENERATED_EDITED = "auto-generated-edited",
  MANUAL = "manual",
}

export type Goal = { level1: GOAL_LEVEL_1; level2: GOAL_LEVEL_2[]; emoji: string };

export const CROP_MIX_COMPOSER_GOALS: Goal[] = [
  {
    level1: GOAL_LEVEL_1.SOIL_HEALTH,
    level2: [GOAL_LEVEL_2.BUILD_CARBON, GOAL_LEVEL_2.SOIL_STRUCTURE_TOPSOIL, GOAL_LEVEL_2.SOIL_STRUCTURE_SUBSOIL, GOAL_LEVEL_2.EROSION_CONTROL],
    emoji: "🪱",
  },
  {
    level1: GOAL_LEVEL_1.NUTRIENT_DYNAMIC,
    level2: [
      GOAL_LEVEL_2.NUTRIENT_BINDING_FALL,
      GOAL_LEVEL_2.NUTRIENT_BINDING_SPRING,
      GOAL_LEVEL_2.NUTRIENT_ACCUMULATION_NITROGEN,
      GOAL_LEVEL_2.NUTRIENT_ACCUMULATION_PHOSPHORUS,
    ],
    emoji: "🔁",
  },
  {
    level1: GOAL_LEVEL_1.WEED_SUPPRESSION,
    level2: [GOAL_LEVEL_2.WEED_SUPPRESSION_GROWTH, GOAL_LEVEL_2.WEED_SUPPRESSION_RESIDUE],
    emoji: "🌿",
  },
  {
    level1: GOAL_LEVEL_1.DROUGHT_RESILIENCE,
    level2: [GOAL_LEVEL_2.DROUGHT_RESILIENCE_GERMINATION, GOAL_LEVEL_2.DROUGHT_RESILIENCE_GROWTH],
    emoji: "☀️",
  },
  {
    level1: GOAL_LEVEL_1.BIODIVERSITY,
    level2: [GOAL_LEVEL_2.INSECTS_AND_WILDLIFE],
    emoji: "🐝",
  },
  {
    level1: GOAL_LEVEL_1.USE_STRATEGY,
    level2: [GOAL_LEVEL_2.FODDER, GOAL_LEVEL_2.GRAZING, GOAL_LEVEL_2.BIOGAS, GOAL_LEVEL_2.CUT_AND_CARRY],
    emoji: "🌾",
  },
];

export type GoalToScoreMap = Record<GOAL_LEVEL_2, number>;

export type RecommendationSettings = {
  soilType: CMC_SOIL_TYPE | undefined;
  soilPh: CMC_PH_TYPE | undefined;
  isDrought: boolean | undefined;
  cropRotationFamiliesToExclude: CROP_ROTATION[];
};

export type MainCropSettingsCMC = {
  crop1Id: string | undefined;
  crop2Id: string | undefined;
  seedingDate: string | undefined;
  harvestDate: string | undefined;
};

export type RecommendationCMC = {
  settings: RecommendationSettings;
  mainCropSettings: MainCropSettingsCMC;
};

export type MutateCropMixComposerPreferencesRequest = {
  name: string;
  goals?: GOAL_LEVEL_2[];
  recommendation?: {
    settings?: RecommendationSettings;
    mainCropSettings?: MainCropSettingsCMC;
  };
  mixType?: CMC_MIX_TYPE;
};

export type CmcScore = { level1: GOAL_LEVEL_1; level2: GOAL_LEVEL_2; score: number };

export type CmcCrop = { id: string; name: string; translationSlug: string; coastCategory: SEED_COST_RATE; cropFamily: CROP_ROTATION };

export type WeightDistributionParams = {
  minShareMix: number;
  maxShareMix: number;
  seedingRateKgHa: number;
  minimumGrowTimeWeeks: number;
};

export type CropMixComposerGoalResponse = {
  crop: CmcCrop;
  scores: CmcScore[];
  matchScore: number;
  autoMixWeightShare: number;
  weightDistributionParams: WeightDistributionParams;
};

export type ScoreFactors = {
  crops: { count: number; score: number }[];
  plantFamilies: { count: number; score: number }[];
};

export type MixDistributionShare = {
  [cropId: string]: {
    name: string;
    weightShare: number;
    weightSeedingRate: number;
    minShareMix: number;
    maxShareMix: number;
    minimumGrowTimeWeeks: number;
  };
};

export type CropMixComposerSettings = {
  id: string;
  farmId: string;
  name: string;
  goals: GOAL_LEVEL_2[];
  recommendation: {
    settings: {
      soilType: CMC_SOIL_TYPE | null;
      soilPh: CMC_PH_TYPE | null;
      isDrought: boolean | null;
      cropRotationFamiliesToExclude: CROP_ROTATION[];
    };
    mainCropSettings: { crop1Id: string | null; crop2Id: string | null; seedingDate: string | null; harvestDate: string | null };
  };
  mixType: CMC_MIX_TYPE | null;
  lastUpdated: string;
};

export type CropMixComposerCrop = {
  id: string;
  mixId: string;
  cropId: string;
  weightShare: number;
};

export type AddCropMixComposerCropsRequestBody = {
  composition: {
    cropId: string;
    weightShare: number;
  }[];
};

export enum CropsOverviewTableColumn {
  COVER_CROP = "coverCrop",
  SEEDING_RATE = "seedingRate",
  WEIGHT_SHARE = "weightShare",
  LOCATION_MATCH = "locationMatch",
}

export type CropsOverviewTableDataType = {
  key: string;
  select?: ReactNode;
  coverCrop: ReactNode;
  family?: ReactNode;
  cost?: ReactNode | null;
  seedingRate?: string;
  weightShare?: string;
  locationMatch: ReactNode;
  goal1: ReactNode;
  goal2: ReactNode;
  goal3: ReactNode;
};

export type CommercialMixOverviewTableDataType = {
  key: string;
  mixName: ReactNode;
  provider: ReactNode;
  matchScoreAndDetails: ReactNode;
};

export type SortDirection = "asc" | "desc" | null;

export type CropsOverviewTableColumnTitleProps = {
  title: string;
  columnKey: string;
  active: boolean;
  direction: SortDirection;
  currentStep: CMC_ROUTES | null;
  onClick: (columnKey: string) => void;
  className?: string;
};

export type CropsOverviewTableColumnConfig = {
  title: string | ReactNode;
  dataIndex: string;
  key: string;
  className?: string;
  width?: string;
};

export type SortState = {
  column: string;
  direction: SortDirection;
};

export type ComponentWithGoalScores = {
  id: string;
  cropMixId: string;
  cropId: string;
  weightProportion: number;
  seedProportion: number;
  name: string;
  cropFamily: CROP_ROTATION;
  goalScores: Array<{
    level1: string;
    level2: string;
    score: number;
  }>;
};

export type CropMixComposerCommercialMix = {
  id: string;
  provider: string;
  link: string | null;
  name: string;
  cropRotationCompatibility: CropRotationCompatibility[];
  strategy: string | null;
  matchScore: number;
  averageGoalScore: number;
  components: ComponentWithGoalScores[];
};

export type AdminCropMixComposerCommercialMixResponse = {
  id: string;
  provider: string;
  link: string | null;
  name: string;
  cropRotationCompatibility: CropRotationCompatibility[];
  strategy: string | null;
  components: AdminCropMixComposerCommercialMixComponentResponse[];
};

export type AdminCropMixComposerCommercialMixComponentResponse = {
  cropMixId: string;
  cropId: string;
  weightProportion: number;
  seedProportion: number;
  crop: PracticeCrop;
};

export type AdminCropMixComposerCommercialMixComponentRequest = {
  cropId: string;
  weightProportion: number;
  seedProportion: number;
};

export type AdminCropMixComposerCommercialMixRequest = {
  id: string;
  provider: string;
  link?: string;
  name: string;
  cropRotationCompatibility?: CropRotationCompatibility[];
  strategy?: string;
  components: AdminCropMixComposerCommercialMixComponentRequest[];
};

export type UpdateCropMixComposerCommercialMixesErrorItem = {
  mixId: string;
  errorItems: StructuredError[];
};

export type UpdateCropMixComposerCommercialMixesResponseBody = {
  success: boolean;
  processedCount: number;
  errorCount: number;
  errors: UpdateCropMixComposerCommercialMixesErrorItem[];
};

export type GoalScore = {
  name: string;
  score: number;
};
